import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './MyPayments.css';
import { Container } from 'react-bootstrap';
import BackButton from '../../components/BackButton/BackButton';
import paymentService, { PaymentTransaction, CreditTransaction, UserCredits } from '../../services/paymentService';
import smartReportsService from '../../services/smartReportsService';
import walletService, { Wallet } from '../../services/walletService';
import { SRTransaction, WalletBalance } from '../../types/smartReports';

type UnifiedTransaction = {
  id: string;
  date: string;
  description: string;
  type: 'credit' | 'debit';
  amount: number;
  status: 'completed' | 'pending' | 'failed';
  source: 'infinicare' | 'smart_reports' | 'wallet';
  has_invoice?: boolean;
  original_txn_id?: string;
  // Set for teleconsultation payments so the row can link back to the booking.
  link?: string;
  link_label?: string;
};

const MyPayments: React.FC = () => {
  const { t } = useTranslation(['services', 'common']);
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions'>('overview');
  const [loading, setLoading] = useState(true);

  // Real data
  const [paymentTxns, setPaymentTxns] = useState<PaymentTransaction[]>([]);
  const [creditTxns, setCreditTxns] = useState<CreditTransaction[]>([]);
  const [srTxns, setSrTxns] = useState<SRTransaction[]>([]);
  const [userCredits, setUserCredits] = useState<UserCredits | null>(null);
  const [srWallet, setSrWallet] = useState<WalletBalance | null>(null);
  const [wallet, setWallet] = useState<Wallet | null>(null);

  useEffect(() => {
    // InfiniCare APIs — fast, local backend
    Promise.allSettled([
      paymentService.getTransactions(),
      paymentService.getCreditHistory(),
      paymentService.getMyCredits(),
      walletService.getWallet(),
    ]).then(results => {
      if (results[0].status === 'fulfilled') setPaymentTxns(results[0].value);
      if (results[1].status === 'fulfilled') setCreditTxns(results[1].value);
      if (results[2].status === 'fulfilled') setUserCredits(results[2].value);
      if (results[3].status === 'fulfilled') setWallet(results[3].value);
      setLoading(false);
    });

    // Smart Reports APIs — external service, may need auth
    Promise.allSettled([
      smartReportsService.getWalletBalance(),
      smartReportsService.getTransactions(1, 50),
    ]).then(results => {
      if (results[0].status === 'fulfilled') setSrWallet(results[0].value);
      if (results[1].status === 'fulfilled') setSrTxns(results[1].value.transactions || []);
    });
  }, []);

  // Unify all transactions into one list
  const allTransactions = useMemo<UnifiedTransaction[]>(() => {
    const unified: UnifiedTransaction[] = [];

    // InfiniCare payment transactions
    const paymentModeLabels: Record<string, string> = {
      NB: 'Net Banking', UPI: 'UPI', CC: 'Credit Card',
      DC: 'Debit Card', WALLET: 'Wallet', EMI: 'EMI',
    };
    paymentTxns.forEach(t => {
      let desc = t.plan_name || t.credit_pack_name || '';
      if (!desc) {
        if (t.product_type === 'gym_voucher') desc = 'Gym Voucher';
        else if (t.product_type === 'appointment') {
          desc = t.appointment_doctor_name
            ? `Tele Consultation - Dr ${t.appointment_doctor_name}`
            : 'Tele Consultation';
        } else {
          desc = 'Payment';
        }
      }
      const mode = t.payment_mode ? (paymentModeLabels[t.payment_mode.toUpperCase()] || t.payment_mode) : '';
      if (mode) desc += ` (${mode})`;

      unified.push({
        id: t.txn_id || `IC-${t.id}`,
        date: t.created_at,
        description: desc,
        type: t.status === 'refunded' ? 'credit' : 'debit',
        amount: parseFloat(t.amount),
        status: t.status === 'success' ? 'completed' : t.status === 'initiated' ? 'pending' : 'failed',
        source: 'infinicare',
        has_invoice: t.has_invoice,
        original_txn_id: t.txn_id,
        link: t.product_type === 'appointment' ? '/teleconsultation?tab=appointments' : undefined,
        link_label: t.product_type === 'appointment' ? 'View booking' : undefined,
      });
    });

    // InfiniCare credit transactions
    creditTxns.forEach(t => {
      unified.push({
        id: `CR-${t.id}`,
        date: t.created_at,
        description: t.description || `${t.transaction_type} credits`,
        type: t.transaction_type === 'usage' ? 'debit' : 'credit',
        amount: Math.abs(t.amount),
        status: 'completed',
        source: 'infinicare',
      });
    });

    // Smart Reports wallet transactions
    srTxns.forEach(t => {
      unified.push({
        id: t.txnid || `SR-${t.id}`,
        date: t.created_at,
        description: t.description || `Smart Reports - ${t.type}`,
        type: t.amount >= 0 ? 'credit' : 'debit',
        amount: Math.abs(t.amount),
        status: t.status === 'success' || t.status === 'completed' ? 'completed' : t.status === 'pending' ? 'pending' : 'failed',
        source: 'smart_reports',
      });
    });

    // Rupee refund-wallet transactions (credits from refunds, debits when spent)
    (wallet?.transactions || []).forEach(t => {
      unified.push({
        id: `WL-${t.id}`,
        date: t.created_at,
        description: t.description || (t.amount >= 0 ? 'Wallet refund' : 'Wallet payment'),
        type: t.amount >= 0 ? 'credit' : 'debit',
        amount: Math.abs(t.amount),
        status: 'completed',
        source: 'wallet',
      });
    });

    // Sort by date descending
    unified.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return unified;
  }, [paymentTxns, creditTxns, srTxns, wallet]);

  const stats = useMemo(() => {
    const totalSpent = allTransactions
      .filter(t => t.type === 'debit' && t.status === 'completed')
      .reduce((s, t) => s + t.amount, 0);
    const totalRefunds = allTransactions
      .filter(t => t.type === 'credit' && t.status === 'completed')
      .reduce((s, t) => s + t.amount, 0);
    const pending = allTransactions
      .filter(t => t.status === 'pending')
      .reduce((s, t) => s + t.amount, 0);
    return { totalSpent, totalRefunds, pending };
  }, [allTransactions]);

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const tabs = [
    { key: 'overview' as const, label: t('services:payments.overview'), icon: '\uD83D\uDCCA' },
    { key: 'transactions' as const, label: t('services:payments.transactions'), icon: '\uD83D\uDCB3' },
  ];

  return (
    <div className="mp-page">
      {/* Welcome Banner */}
      <section className="mp-welcome-banner">
        <Container>
          <BackButton />
          <div className="mp-welcome-layout">
            <div className="mp-welcome-text">
              <h1 className="mp-welcome-title">
                {'\uD83D\uDCB0'} {t('services:payments.title')}
              </h1>
              <p className="mp-welcome-subtitle">
                {t('services:payments.subtitle')}
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* Stats Row — glassmorphism cards overlapping banner */}
      <Container>
        <div className="mp-stats-section">
          <div className="mp-stats-row">
            <div className="mp-stat-card">
              <div className="mp-stat-icon mp-stat-icon-spent">
                <span>{'\uD83D\uDCE4'}</span>
              </div>
              <div className="mp-stat-info">
                <span className="mp-stat-value">{'\u20B9'}{stats.totalSpent.toLocaleString()}</span>
                <span className="mp-stat-label">{t('services:payments.totalSpent')}</span>
              </div>
            </div>
            <div className="mp-stat-card">
              <div className="mp-stat-icon mp-stat-icon-pending">
                <span>{'\u23F3'}</span>
              </div>
              <div className="mp-stat-info">
                <span className="mp-stat-value">{'\u20B9'}{stats.pending.toLocaleString()}</span>
                <span className="mp-stat-label">{t('services:payments.pending')}</span>
              </div>
            </div>
            <div className="mp-stat-card">
              <div className="mp-stat-icon mp-stat-icon-wallet">
                <span>{'\uD83D\uDC5B'}</span>
              </div>
              <div className="mp-stat-info">
                <span className="mp-stat-value">{'\u20B9'}{wallet ? wallet.balance.toLocaleString() : '0'}</span>
                <span className="mp-stat-label">{t('services:payments.refundWallet', 'InfiniCare Wallet')}</span>
              </div>
            </div>
            <div className="mp-stat-card">
              <div className="mp-stat-icon mp-stat-icon-insight">
                <span>{'\u2B50'}</span>
              </div>
              <div className="mp-stat-info">
                <span className="mp-stat-value">{userCredits ? userCredits.balance : '—'}</span>
                <span className="mp-stat-label">{t('services:payments.insightCredits')}</span>
              </div>
            </div>
            <div className="mp-stat-card">
              <div className="mp-stat-icon mp-stat-icon-sr">
                <span>{'\uD83E\uDDEA'}</span>
              </div>
              <div className="mp-stat-info">
                <span className="mp-stat-value">{srWallet ? srWallet.balance.toFixed(1) : '—'}</span>
                <span className="mp-stat-label">{t('services:payments.srCredits')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mp-tabs-container">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`mp-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="mp-tab-icon">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="mp-tab-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="mp-overview-tab">
              <div className="mp-tab-header mp-tab-header-overview">
                <div className="mp-tab-header-text">
                  <h2>
                    <span className="mp-tab-header-icon">{'\uD83D\uDCCA'}</span>
                    {t('services:payments.spendingSummary')}
                  </h2>
                  <p>{t('services:payments.spendingSummaryDesc')}</p>
                </div>
              </div>

              {loading ? (
                <div className="mp-loading">{t('services:payments.loadingTransactions')}</div>
              ) : (
                <>
                  <div className="mp-summary-grid">
                    <div className="mp-summary-card">
                      <div className="mp-summary-icon mp-summary-icon-debit">
                        <span>{'\uD83D\uDCE4'}</span>
                      </div>
                      <div className="mp-summary-info">
                        <span className="mp-summary-label">{t('services:payments.totalSpent')}</span>
                        <span className="mp-summary-value">{'\u20B9'}{stats.totalSpent.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="mp-summary-card">
                      <div className="mp-summary-icon mp-summary-icon-credit">
                        <span>{'\uD83D\uDCE5'}</span>
                      </div>
                      <div className="mp-summary-info">
                        <span className="mp-summary-label">{t('services:payments.totalRefunds')}</span>
                        <span className="mp-summary-value">{'\u20B9'}{stats.totalRefunds.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="mp-summary-card">
                      <div className="mp-summary-icon mp-summary-icon-pending">
                        <span>{'\u23F3'}</span>
                      </div>
                      <div className="mp-summary-info">
                        <span className="mp-summary-label">{t('services:payments.pendingPayments')}</span>
                        <span className="mp-summary-value">{'\u20B9'}{stats.pending.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Recent Transactions Preview */}
                  <div className="mp-recent-header">
                    <h3>{t('services:payments.recentActivity')}</h3>
                    <button className="mp-view-all-btn" onClick={() => setActiveTab('transactions')}>
                      {t('services:payments.viewAll')} {'\u2192'}
                    </button>
                  </div>
                  {allTransactions.length === 0 ? (
                    <div className="mp-empty-state">{t('services:payments.noTransactionsYet')}</div>
                  ) : (
                    <div className="mp-recent-list">
                      {allTransactions.slice(0, 5).map((txn) => (
                        <div className={`mp-txn-card mp-txn-card-${txn.type}`} key={txn.id}>
                          <div className="mp-txn-icon-wrapper">
                            <span className={`mp-txn-icon mp-txn-icon-${txn.type}`}>
                              {txn.type === 'credit' ? '\u2193' : '\u2191'}
                            </span>
                          </div>
                          <div className="mp-txn-details">
                            <div className="mp-txn-description">{txn.description}</div>
                            <div className="mp-txn-meta">
                              <span className="mp-txn-id">{txn.id}</span>
                              <span className="mp-txn-date">{formatDate(txn.date)}</span>
                              {txn.source === 'smart_reports' && (
                                <span className="mp-txn-source-badge">Smart Reports</span>
                              )}
                              {txn.source === 'wallet' && (
                                <span className="mp-txn-source-badge mp-txn-source-wallet">InfiniCare Wallet</span>
                              )}
                              {txn.link && (
                                <Link to={txn.link} className="mp-txn-link">{txn.link_label}</Link>
                              )}
                            </div>
                          </div>
                          <div className="mp-txn-amount-section">
                            <span className={`mp-txn-amount mp-txn-amount-${txn.type}`}>
                              {txn.type === 'credit' ? '+' : '-'}{'\u20B9'}{txn.amount.toLocaleString()}
                            </span>
                            <div className="mp-txn-status-row">
                              <span className={`mp-txn-status mp-txn-status-${txn.status}`}>
                                {txn.status === 'completed' ? `\u2713 ${t('services:payments.completedStatus')}` : txn.status === 'pending' ? `\u23F3 ${t('services:payments.pendingStatus')}` : `\u2715 ${t('services:payments.failedStatus')}`}
                              </span>
                              {txn.has_invoice && txn.original_txn_id && (
                                <button
                                  className="mp-invoice-btn"
                                  title="Download Invoice"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    paymentService.downloadInvoice(txn.original_txn_id!);
                                  }}
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                    <polyline points="7 10 12 15 17 10"/>
                                    <line x1="12" y1="15" x2="12" y2="3"/>
                                  </svg>
                                  Invoice
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <div className="mp-transactions-tab">
              <div className="mp-tab-header mp-tab-header-transactions">
                <div className="mp-tab-header-text">
                  <h2>
                    <span className="mp-tab-header-icon">{'\uD83D\uDCB3'}</span>
                    {t('services:payments.allTransactions')}
                  </h2>
                  <p>{t('services:payments.allTransactionsDesc')}</p>
                </div>
              </div>

              {loading ? (
                <div className="mp-loading">{t('services:payments.loadingTransactions')}</div>
              ) : allTransactions.length === 0 ? (
                <div className="mp-empty-state">{t('services:payments.noTransactions')}</div>
              ) : (
                <div className="mp-transactions-list">
                  <div className="mp-list-header">
                    <span>{t('services:payments.transactionHistory', { count: allTransactions.length })}</span>
                  </div>

                  {allTransactions.map((txn) => (
                    <div className={`mp-txn-card mp-txn-card-${txn.type}`} key={txn.id}>
                      <div className="mp-txn-icon-wrapper">
                        <span className={`mp-txn-icon mp-txn-icon-${txn.type}`}>
                          {txn.type === 'credit' ? '\u2193' : '\u2191'}
                        </span>
                      </div>
                      <div className="mp-txn-details">
                        <div className="mp-txn-description">{txn.description}</div>
                        <div className="mp-txn-meta">
                          <span className="mp-txn-id">{txn.id}</span>
                          <span className="mp-txn-date">{formatDate(txn.date)}</span>
                          {txn.source === 'smart_reports' && (
                            <span className="mp-txn-source-badge">Smart Reports</span>
                          )}
                          {txn.source === 'wallet' && (
                            <span className="mp-txn-source-badge mp-txn-source-wallet">InfiniCare Wallet</span>
                          )}
                          {txn.link && (
                            <Link to={txn.link} className="mp-txn-link">{txn.link_label}</Link>
                          )}
                        </div>
                      </div>
                      <div className="mp-txn-amount-section">
                        <span className={`mp-txn-amount mp-txn-amount-${txn.type}`}>
                          {txn.type === 'credit' ? '+' : '-'}{'\u20B9'}{txn.amount.toLocaleString()}
                        </span>
                        <div className="mp-txn-status-row">
                          <span className={`mp-txn-status mp-txn-status-${txn.status}`}>
                            {txn.status === 'completed' ? `\u2713 ${t('services:payments.completedStatus')}` : txn.status === 'pending' ? `\u23F3 ${t('services:payments.pendingStatus')}` : `\u2715 ${t('services:payments.failedStatus')}`}
                          </span>
                          {txn.has_invoice && txn.original_txn_id && (
                            <button
                              className="mp-invoice-btn"
                              title="Download Invoice"
                              onClick={(e) => {
                                e.stopPropagation();
                                paymentService.downloadInvoice(txn.original_txn_id!);
                              }}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="7 10 12 15 17 10"/>
                                <line x1="12" y1="15" x2="12" y2="3"/>
                              </svg>
                              Invoice
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default MyPayments;
